import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Money } from '@/components/money/Money.js';
import { cn } from '@/lib/utils/cn.js';

export type TxTone = 'credit' | 'debit' | 'neutral';

const TONE: Record<TxTone, { amount: string; icon: string; sign: string }> = {
  credit:  { amount: 'text-[var(--success)]',      icon: 'bg-[var(--success)]/15 text-[var(--success)]',        sign: '+ ' },
  debit:   { amount: 'text-[var(--text-primary)]', icon: 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]', sign: '− ' },
  neutral: { amount: 'text-[var(--text-muted)]',   icon: 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]', sign: ''   },
};

interface TransactionRowBase {
  icon: LucideIcon;
  title: ReactNode;
  subtitle: ReactNode;
  /** Optional trailing chip beside the title (e.g. a deposit/withdraw status pill). */
  badge?: ReactNode;
  /**
   * A second subtitle line, for a fact about the amount rather than about the
   * row's identity (e.g. the bonus a deposit earned). Its own line because on a
   * phone the subtitle is already at its truncation width.
   */
  note?: ReactNode;
  /** Minor units; rendered as its magnitude — the leading sign comes from `tone`. */
  amountMinor: number;
  currency: string;
  tone?: TxTone;
  hasBorder?: boolean;
}

/**
 * A row is either pressable as a whole (`onClick`) or hosts its own control
 * (`action`, e.g. cancelling a deposit request) — never both. A pressable row
 * renders as a `<button>`, and a second button nested inside one is invalid
 * HTML whose inner control stops receiving clicks; the union makes that a
 * typecheck failure rather than something to discover in a browser.
 */
type TransactionRowProps = TransactionRowBase &
  (
    | { onClick?: () => void; action?: never }
    | { onClick?: never; action?: ReactNode }
  );

export function TransactionRow({
  icon: Icon,
  title,
  subtitle,
  badge,
  note,
  action,
  amountMinor,
  currency,
  tone = 'neutral',
  onClick,
  hasBorder = true,
}: TransactionRowProps) {
  const t = TONE[tone];
  const cls = cn(
    'w-full grid items-center px-4 py-3 gap-3 text-left',
    action ? 'grid-cols-[36px_1fr_auto_auto]' : 'grid-cols-[36px_1fr_auto]',
    onClick && 'transition-colors hover:bg-[var(--bg-elevated)]/50',
    hasBorder && 'border-t border-[var(--border-subtle)]',
  );
  const body = (
    <>
      <div className={cn('w-9 h-9 rounded-full grid place-items-center shrink-0', t.icon)}>
        <Icon className="w-[18px] h-[18px]" strokeWidth={2} aria-hidden />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[13px] font-semibold text-[var(--text-primary)] truncate">{title}</span>
          {badge}
        </div>
        <div className="text-[11px] text-[var(--text-muted)] truncate">{subtitle}</div>
        {note && <div className="text-[11px] truncate mt-0.5">{note}</div>}
      </div>
      <div className={cn('font-bold text-sm bf-tnum shrink-0', t.amount)}>
        {t.sign}
        <Money minor={Math.abs(amountMinor)} currency={currency} />
      </div>
      {action}
    </>
  );

  return onClick ? (
    <button type="button" onClick={onClick} className={cls}>
      {body}
    </button>
  ) : (
    <div className={cls}>{body}</div>
  );
}
